/**
 * Label designer model (Claude Design «Etikettdesigner Dialog»).
 * Pure helpers: media, per-module fields, default layouts, templates, Code 39.
 * All geometry is in millimetres; rendering scales it to pixels.
 */

export const PRINTERS = ["QL-700", "QL-1100"];

export const MEDIA = [
  { id: "DK-22205", w: 62, cont: true, len: 29, printers: ["QL-700", "QL-1100"] },
  { id: "DK-11209", w: 62, h: 29, printers: ["QL-700", "QL-1100"] },
  { id: "DK-11201", w: 90, h: 29, printers: ["QL-700", "QL-1100"] },
  { id: "DK-11208", w: 90, h: 38, printers: ["QL-700", "QL-1100"] },
  { id: "DK-11204", w: 54, h: 17, printers: ["QL-700", "QL-1100"] },
  { id: "DK-22243", w: 102, cont: true, len: 60, printers: ["QL-1100"] },
  { id: "DK-11241", w: 102, h: 152, printers: ["QL-1100"] },
];

/** Same die-cut size as the old API label type (90 × 29 mm). */
export const DEFAULT_MEDIA_ID = "DK-11201";
export const MIN_LENGTH_MM = 12;
export const MAX_LENGTH_MM = 300;
export const PRINTER_DPI = 300;
/** 1 pt in mm. */
export const PT_MM = 0.3528;
/** Brother QL safe print inset in mm. */
export const SAFE_INSET_MM = 1.5;

export const FONT_FAMILIES = {
  sans: "'IBM Plex Sans', system-ui, sans-serif",
  cond: "'IBM Plex Sans Condensed', 'IBM Plex Sans', sans-serif",
  mono: "'IBM Plex Mono', ui-monospace, monospace",
};

/**
 * Fields per module. `key` is the template token ({key}); `label` is an i18n key.
 * Every module has nr/navn/url/dato so shared layouts work everywhere.
 */
export const MODULE_FIELDS = {
  product: ["navn", "nr", "pris", "beskrivelse", "merke", "kategori", "lokasjon", "url", "dato"],
  project: ["nr", "navn", "kunde", "beskrivelse", "url", "dato"],
  report: ["nr", "navn", "kunde", "adresse", "url", "dato"],
  tool: ["nr", "navn", "modell", "serienr", "merke", "url", "dato"],
  document: ["nr", "navn", "beskrivelse", "info", "url", "dato"],
};

export const LABEL_MODULES = Object.keys(MODULE_FIELDS);

export function fieldsForModule(module) {
  return MODULE_FIELDS[module] || MODULE_FIELDS.product;
}

export function mediaById(id) {
  return MEDIA.find((m) => m.id === id) || MEDIA.find((m) => m.id === DEFAULT_MEDIA_ID);
}

export function mediaForPrinter(printer) {
  return MEDIA.filter((m) => m.printers.includes(printer));
}

export function clampLength(value) {
  const n = parseFloat(value);
  if (!Number.isFinite(n)) return MIN_LENGTH_MM;
  return Math.max(MIN_LENGTH_MM, Math.min(MAX_LENGTH_MM, n));
}

/** [width, height] in mm for the medium (continuous tape uses the chosen length). */
export function labelDims(media, length) {
  return [media.w, media.cont ? clampLength(length) : media.h];
}

export function mmToDots(mm, dpi = PRINTER_DPI) {
  return Math.round((mm / 25.4) * dpi);
}

export function resolveTemplate(template, record) {
  return String(template || "").replace(/\{(\w+)\}/g, (_, key) => {
    const value = record?.[key];
    return value == null ? "" : String(value);
  });
}

/**
 * Collapse separators left behind by empty tokens, e.g. "{nr}  ·  {lokasjon}"
 * with no location becomes "SKR-1" instead of "SKR-1  ·  ".
 */
export function tidyResolved(text) {
  return String(text)
    .split("\n")
    .map((line) =>
      line
        .replace(/^(\s*[·|]\s*)+/, "")
        .replace(/(\s*[·|]\s*)+$/, "")
        .replace(/(\s+·\s+)(\s*·\s*)+/g, "$1")
    )
    .join("\n")
    .trim();
}

export function hasTokens(template) {
  return /\{\w+\}/.test(template || "");
}

/** Text an element shows for a record: manual override wins over the template. */
export function elementText(el, record) {
  if (el.override != null) return String(el.override);
  return tidyResolved(resolveTemplate(el.template, record));
}

let uid = 0;
export function newElementId() {
  uid += 1;
  return `el${Date.now().toString(36)}${uid}`;
}

const text = (o) => ({
  id: newElementId(),
  type: "text",
  override: null,
  font: "sans",
  weight: 400,
  align: "left",
  fit: true,
  size: 10,
  ...o,
});
const code = (o) => ({ id: newElementId(), override: null, ...o });

/**
 * Standard layout for a module and label size. Products get a Code 39 barcode
 * of the item number (as in the design); the other modules get a QR code that
 * opens the record in Promang.
 */
export function autoLayout(module, W, H) {
  const p = Math.max(SAFE_INSET_MM, Math.min(W, H) * 0.07);
  const iw = W - 2 * p;
  const isProduct = (module || "product") === "product";
  const line2 = {
    product: "{nr}  ·  {lokasjon}",
    project: "{nr}  ·  {kunde}",
    report: "{nr}  ·  {kunde}",
    tool: "{nr}  ·  {modell}",
    document: "{nr}  ·  {info}",
  }[module] || "{nr}";
  const detail = { project: "{beskrivelse}", report: "{adresse}", tool: "{serienr}", document: "{beskrivelse}" }[module] || "";

  if (H < 22) {
    return [
      text({ x: p, y: SAFE_INSET_MM, w: iw, h: H * 0.48, template: "{navn}", weight: 600, size: 14 }),
      text({ x: p, y: H * 0.58, w: iw, h: H * 0.3, template: line2, font: "mono", size: 8 }),
    ];
  }

  if (H <= 45) {
    if (isProduct) {
      return [
        text({ x: p, y: p, w: iw, h: H * 0.26, template: "{navn}", weight: 600, size: 18 }),
        text({ x: p, y: p + H * 0.28, w: iw, h: H * 0.14, template: line2, font: "mono", size: 8 }),
        code({ type: "barcode", x: p, y: H * 0.5, w: W * 0.56, h: H * 0.5 - p, template: "{nr}", showText: false }),
        text({ x: W * 0.6, y: H * 0.5, w: W * 0.4 - p, h: H * 0.27, template: "{pris}", weight: 700, align: "right", size: 22, font: "cond" }),
        text({ x: W * 0.6, y: H * 0.78, w: W * 0.4 - p, h: H * 0.13, template: "{merke}", align: "right", size: 8 }),
      ];
    }
    const q = H - 2 * p;
    const tw = W - 3 * p - q;
    return [
      text({ x: p, y: p, w: tw, h: H * 0.3, template: "{nr}", weight: 700, size: 20, font: "cond" }),
      text({ x: p, y: p + H * 0.32, w: tw, h: H * 0.2, template: "{navn}", weight: 600, size: 11 }),
      text({ x: p, y: p + H * 0.54, w: tw, h: H * 0.16, template: line2.replace("{nr}  ·  ", ""), size: 8 }),
      text({ x: p, y: p + H * 0.72, w: tw, h: H * 0.14, template: detail, size: 7 }),
      code({ type: "qr", x: W - p - q, y: p, w: q, h: q, template: "{url}" }),
    ];
  }

  const barcodeY = H * 0.74;
  return [
    text({ x: p, y: p, w: iw, h: H * 0.06, template: isProduct ? "{merke}  ·  {kategori}" : line2.replace("{nr}  ·  ", ""), size: 10, font: "cond" }),
    code({ type: "line", x: p, y: p + H * 0.075, w: iw, h: 0.6, template: "" }),
    text({ x: p, y: p + H * 0.1, w: iw, h: H * 0.22, template: "{navn}", weight: 700, size: 40 }),
    text({ x: p, y: H * 0.38, w: iw * 0.5, h: H * 0.06, template: "{nr}", font: "mono", size: 11 }),
    text({ x: p + iw * 0.5, y: H * 0.38, w: iw * 0.5, h: H * 0.06, template: isProduct ? "{lokasjon}" : "{dato}", font: "mono", size: 11, align: "right" }),
    text({ x: p, y: H * 0.48, w: iw, h: H * 0.16, template: isProduct ? "{pris}" : detail, weight: isProduct ? 700 : 400, size: isProduct ? 60 : 14, font: isProduct ? "cond" : "sans" }),
    isProduct
      ? code({ type: "barcode", x: p, y: barcodeY, w: iw, h: H - barcodeY - p, template: "{nr}", showText: true })
      : code({ type: "qr", x: W - p - (H - barcodeY - p), y: barcodeY, w: H - barcodeY - p, h: H - barcodeY - p, template: "{url}" }),
  ];
}

/* ---------------- Code 39 ---------------- */

const C39 = {
  0: "nnnwwnwnn", 1: "wnnwnnnnw", 2: "nnwwnnnnw", 3: "wnwwnnnnn", 4: "nnnwwnnnw",
  5: "wnnwwnnnn", 6: "nnwwwnnnn", 7: "nnnwnnwnw", 8: "wnnwnnwnn", 9: "nnwwnnwnn",
  A: "wnnnnwnnw", B: "nnwnnwnnw", C: "wnwnnwnnn", D: "nnnnwwnnw", E: "wnnnwwnnn",
  F: "nnwnwwnnn", G: "nnnnnwwnw", H: "wnnnnwwnn", I: "nnwnnwwnn", J: "nnnnwwwnn",
  K: "wnnnnnnww", L: "nnwnnnnww", M: "wnwnnnnwn", N: "nnnnwnnww", O: "wnnnwnnwn",
  P: "nnwnwnnwn", Q: "nnnnnnwww", R: "wnnnnnwwn", S: "nnwnnnwwn", T: "nnnnwnwwn",
  U: "wwnnnnnnw", V: "nwwnnnnnw", W: "wwwnnnnnn", X: "nwnnwnnnw", Y: "wwnnwnnnn",
  Z: "nwwnwnnnn", "-": "nwnnnnwnw", ".": "wwnnnnwnn", " ": "nwwnnnwnn", "*": "nwnnwnwnn",
};

/** Characters Code 39 can encode (uppercased; others are dropped). */
export function code39Data(value) {
  return String(value || "")
    .toUpperCase()
    .replace(/[^0-9A-Z\-. ]/g, "");
}

/**
 * Bars for Code 39 as [{x, w}] in module units, plus total width.
 * Wide = 2.5 × narrow, 1 narrow gap between characters.
 */
export function code39Bars(value) {
  const data = code39Data(value);
  if (!data) return { bars: [], width: 0, chars: 0 };
  const bars = [];
  let x = 0;
  for (const ch of `*${data}*`) {
    const pattern = C39[ch];
    for (let i = 0; i < 9; i += 1) {
      const w = pattern[i] === "w" ? 2.5 : 1;
      if (i % 2 === 0) bars.push({ x, w });
      x += w;
    }
    x += 1;
  }
  return { bars, width: x - 1, chars: data.length };
}

/* ---------------- Text fitting ---------------- */

let measureCtx = null;
function measureContext() {
  if (measureCtx !== null) return measureCtx;
  try {
    measureCtx = typeof document !== "undefined" ? document.createElement("canvas").getContext("2d") : false;
  } catch (_) {
    measureCtx = false;
  }
  return measureCtx;
}

/** Width of the widest line at font-size 1 (unitless), and line count. */
export function measureText(value, font, weight) {
  const lines = String(value).split("\n");
  const ctx = measureContext();
  if (!ctx) {
    // jsdom / no canvas: approximate with average glyph width.
    const avg = font === "mono" ? 0.6 : font === "cond" ? 0.46 : 0.53;
    return { w: Math.max(...lines.map((l) => l.length)) * avg, lines: lines.length };
  }
  ctx.font = `${weight} 100px ${FONT_FAMILIES[font] || FONT_FAMILIES.sans}`;
  return { w: Math.max(...lines.map((l) => ctx.measureText(l).width)) / 100, lines: lines.length };
}

export const LINE_HEIGHT = 1.12;

/** Font size in mm for a text element, shrunk to fit its box when `fit` is on. */
export function fittedFontMm(el, value) {
  const base = (Number(el.size) || 10) * PT_MM;
  if (!el.fit || !value) return base;
  const m = measureText(value, el.font, el.weight);
  const byWidth = m.w > 0 ? (el.w * 0.98) / m.w : base;
  const byHeight = el.h / (LINE_HEIGHT * m.lines);
  return Math.max(0.5, Math.min(base, byWidth, byHeight));
}
